import { AlertCircle, CheckCircle, Eye } from "lucide-react";

const DuplicateConfirmModal = ({
  t,
  theme,
  language,
  isLoading,
  selectedRequest,
  duplicateExistingDoc,
  pendingApproveId,
  canViewDocuments = true,
  onApproveReplace,
  onDismiss,
  onPreview,
}) => {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
        zIndex: 12000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onDismiss}
    >
      <div
        style={{
          background:
            theme === "dark"
              ? "linear-gradient(135deg,#0f172a 0%,#1a2a3a 100%)"
              : "linear-gradient(135deg,#fff 0%,#f8fafc 100%)",
          border:
            theme === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(148,163,184,0.2)",
          borderRadius: 16,
          padding: "28px 28px 24px",
          width: "100%",
          maxWidth: 520,
          color: theme === "dark" ? "#e2e8f0" : "#0f172a",
          boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "rgba(245,158,11,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AlertCircle size={20} color="#f59e0b" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
              {t.duplicateTitle}
            </h3>
            <p
              style={{
                margin: "3px 0 0",
                fontSize: 12,
                color: theme === "dark" ? "#94a3b8" : "#64748b",
              }}
            >
              {t.duplicateSubtitle}
            </p>
          </div>
        </div>

        {/* Existing Doc Info */}
        <div
          style={{
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 12,
            background:
              theme === "dark"
                ? "rgba(239,68,68,0.08)"
                : "rgba(239,68,68,0.05)",
            border: "1px solid rgba(239,68,68,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                display: "inline-block",
                padding: "2px 10px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(239,68,68,0.15)",
                color: "#ef4444",
                textTransform: "uppercase",
              }}
            >
              {t.duplicateCurrent}
            </span>
            {duplicateExistingDoc?.id && canViewDocuments && (
              <button
                onClick={() => onPreview(duplicateExistingDoc.id)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <Eye size={13} />
                {t.preview}
              </button>
            )}
          </div>

          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {duplicateExistingDoc?.type}
          </span>

          {duplicateExistingDoc?.created_at && (
            <span
              style={{
                fontSize: 11,
                color: theme === "dark" ? "#64748b" : "#94a3b8",
              }}
            >
              {t.duplicateUploaded}{" "}
              {new Date(duplicateExistingDoc.created_at).toLocaleDateString(
                language === "ar" ? "ar-EG" : "en-US"
              )}
            </span>
          )}
        </div>

        {/* New Upload Info */}
        <div
          style={{
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 20,
            background:
              theme === "dark"
                ? "rgba(59,130,246,0.08)"
                : "rgba(59,130,246,0.05)",
            border: "1px solid rgba(59,130,246,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <span
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700,
              background: "rgba(59,130,246,0.15)",
              color: "#3b82f6",
              textTransform: "uppercase",
            }}
          >
            {t.duplicateNew}
          </span>

          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {selectedRequest?.requestType?.replace("upload:", "")}
          </span>

          {selectedRequest?.requestedDate && (
            <span
              style={{
                fontSize: 11,
                color: theme === "dark" ? "#64748b" : "#94a3b8",
              }}
            >
              {t.duplicateRequested}{" "}
              {new Date(selectedRequest.requestedDate).toLocaleDateString(
                language === "ar" ? "ar-EG" : "en-US"
              )}
            </span>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onDismiss}
            style={{
              flex: 1,
              padding: "11px 0",
              borderRadius: 10,
              cursor: "pointer",
              background:
                theme === "dark"
                  ? "rgba(30,41,59,0.8)"
                  : "rgba(241,245,249,0.9)",
              color: theme === "dark" ? "#cbd5e1" : "#475569",
              border:
                theme === "dark"
                  ? "1.5px solid rgba(255,255,255,0.15)"
                  : "1.5px solid rgba(0,0,0,0.12)",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {t.cancel}
          </button>

          <button
            disabled={isLoading}
            onClick={() => onApproveReplace(pendingApproveId)}
            style={{
              flex: 1,
              padding: "11px 0",
              borderRadius: 10,
              cursor: isLoading ? "not-allowed" : "pointer",
              background: "linear-gradient(135deg,#2e7cba,#1d4ed8)",
              color: "#fff",
              border: "none",
              fontWeight: 700,
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            <CheckCircle size={14} />
            {isLoading ? t.duplicateProcessing : t.duplicateApproveReplace}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateConfirmModal;